import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lightning, FileText, Package, ChartBar, ArrowRight } from '@phosphor-icons/react';

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0f1c] via-[#111827] to-[#0a1628] text-white overflow-hidden relative">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      {/* Glow effects */}
      <div className="absolute top-[-200px] right-[-100px] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-150px] left-[-100px] w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-[100px]" />

      {/* Header */}
      <header className="relative z-10 px-6 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Lightning size={20} weight="bold" className="text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight">Ease Quote</span>
        </div>
        {user ? (
          <button onClick={() => navigate('/dashboard')} className="px-5 py-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-lg text-sm font-medium hover:bg-white/15 transition-all">
            Go to Dashboard
          </button>
        ) : (
          <button onClick={() => navigate('/login')} className="px-5 py-2 bg-blue-600 rounded-lg text-sm font-medium hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/25">
            Sign In
          </button>
        )}
      </header>

      {/* Hero Section */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-xs text-blue-300 font-medium mb-6">
            <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
            Fast Electrical Quotations
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
            Create Professional
            <span className="block mt-1 bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Quotations in Seconds
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-gray-400 max-w-xl mx-auto leading-relaxed">
            Smart product lookup, area-wise BOQ, auto-pricing, PDF exports and WhatsApp sharing — built for electrical distributors.
          </p>
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={() => navigate(user ? '/dashboard' : '/login')}
              className="group px-7 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl text-sm font-semibold shadow-xl shadow-blue-600/25 hover:shadow-blue-600/40 transition-all flex items-center gap-2"
              data-testid="home-get-started"
            >
              Get Started <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/catalog')}
              className="px-7 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 transition-all"
              data-testid="home-catalog"
            >
              Catalog Lookup
            </button>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Lightning, title: 'Smart Lookup', desc: 'Type code, get price instantly with brand selection', color: 'from-blue-500 to-blue-600' },
            { icon: FileText, title: 'Area-wise BOQ', desc: 'Room partitions with independent brand choices', color: 'from-indigo-500 to-indigo-600' },
            { icon: Package, title: 'Bulk Upload', desc: 'Import entire price lists via Excel in seconds', color: 'from-purple-500 to-purple-600' },
            { icon: ChartBar, title: 'Billing Insights', desc: 'Track billed vs non-billed with brand analytics', color: 'from-emerald-500 to-emerald-600' },
          ].map((f, i) => (
            <div key={i} className="p-5 bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-xl hover:border-white/10 hover:bg-white/[0.05] transition-all group">
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${f.color} flex items-center justify-center mb-3 shadow-lg group-hover:scale-105 transition-transform`}>
                <f.icon size={18} weight="bold" className="text-white" />
              </div>
              <h3 className="text-sm font-semibold mb-1">{f.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-5 text-center">
        <p className="text-xs text-gray-600">Ease Quote — Quotation Management for Electrical Distributors</p>
      </footer>
    </div>
  );
}
